import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

// Enemy types for different stages (Shared logic)
// Enemy types for different stages (Shared logic)
export const ENEMIES_FOR_STORE = [
    { id: 'slime', name: 'Fieser Blob', emoji: '🟢', basePower: 100, goldReward: 50, expReward: 20, baseHp: 80, baseAtk: 12, baseDef: 2, baseSpd: 10 },
    { id: 'skeleton', name: 'Skelett', emoji: '💀', basePower: 1000, goldReward: 500, expReward: 100, baseHp: 600, baseAtk: 90, baseDef: 30, baseSpd: 25 },
    { id: 'knight', name: 'Elite Ritter', emoji: '🤺', basePower: 10000, goldReward: 5000, expReward: 800, baseHp: 6000, baseAtk: 800, baseDef: 300, baseSpd: 45 },
    { id: 'guardian', name: 'Tempel Wächter', emoji: '🗿', basePower: 100000, goldReward: 50000, expReward: 5000, baseHp: 70000, baseAtk: 7500, baseDef: 2500, baseSpd: 70 },
    { id: 'dragon', name: 'Drachenlord', emoji: '🐉', basePower: 1000000, goldReward: 250000, expReward: 20000, baseHp: 800000, baseAtk: 60000, baseDef: 15000, baseSpd: 100 },
]

// ============================================
// TYPES
// ============================================

export type GameScreen =
    | 'landing'
    | 'smsintro'
    | 'homescreen'
    | 'setup'
    | 'loading'
    | 'tutorial'
    | 'city'
    | 'hero'
    | 'shop'
    | 'battle'
    | 'ranking'
    | 'end'
    | 'leaderboard'
    | 'referral'
    | 'daily'
    | 'gacha'
    | 'vip'
    | 'battlepass'
    | 'event'
    | 'bank'

export type GamePhase = 'pre-game' | 'tutorial' | 'midgame' | 'endgame' | 'finished'

// Extended Tutorial Phases
export type TutorialPhase =
    | 'not-started'  // Before skip choice
    | 'skipped'      // User skipped tutorial
    | 'building'     // Phase 1: Rathaus upgrade
    | 'shop'         // Phase 2: Go to shop
    | 'gacha'        // Phase 3: Open lootbox
    | 'hero'         // Phase 4: Equip item
    | 'battle'       // Phase 5: PvE fight
    | 'daily'        // Phase 6: Daily bonus
    | 'bank'         // Phase 7: Ask Oma
    | 'pvp'          // Phase 8: PvP fight
    | 'finale'       // Phase 9: Finale screen
    | 'complete'     // Done

export interface Building {
    id: string
    name: string
    description?: string
    emoji: string
    level: number
    maxLevel: number
    upgradeTimeSeconds: number
    upgradeCostGold: number
    upgradeCostGems: number
    powerBonus: number
    // Special stats
    productionRate?: number // Gold/min
    capacity?: number // Item count
    energyMax?: number // Energy bonus
}

export type ItemRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary'

export interface Item {
    id: string
    name: string
    emoji: string
    slot: string
    power: number
    rarity: ItemRarity
}

export interface ResourceBag {
    gold: number
    gems: number
    euro: number
    energy: number
    maxEnergy: number
}

// ============================================
// GAME STATE
// ============================================

interface GameState {
    // Current Screen & Phase
    currentScreen: GameScreen
    currentPhase: GamePhase

    // Player Resources
    resources: ResourceBag
    power: number

    // Buildings
    buildings: Building[]
    activeUpgrades: { [id: string]: number } // timestamp when upgrade finishes

    // Progress
    tutorialStep: number
    tutorialComplete: boolean

    // Extended Tutorial System
    tutorialPhase: TutorialPhase
    tutorialSubStep: number  // Sub-step within a phase
    tutorialWonItem: Item | null  // Track item won in gacha for equip phase

    // Ranking
    currentRank: number
    targetRank: number

    // Speedrun Tracking
    gameStartTime: number | null
    realMoneySpent: number
    adsWatched: number
    totalWaitTimeMs: number

    // Game Time (Accelerated: 1 min real = 1 hour game)
    totalGameMinutes: number

    // VIP Status
    isVIP: boolean
    vipLevel: number // 1, 2, or 3
    vipExpiresAtGameMinute: number | null // Total minute when VIP ends

    // Free Lootbox Spins (from bundles)
    freeLootboxSpins: number

    // Daily Rewards
    dailyStreak: number
    lastDailyClaimGameDay: number | null // Day number when last claimed
    lastDailyClaimType: 'free' | 'doubled' | null // 'free' = claimed normal, 'doubled' = claimed VIP bonus too
    claimDailyReward: () => void
    upgradeDailyClaimToDoubled: () => void // For when user buys VIP after claiming free

    // Shop Navigation
    activeShopTab: 'bundles' | 'packs' | 'passes' | 'skins'
    setShopTab: (tab: 'bundles' | 'packs' | 'passes' | 'skins') => void

    // Battle Pass
    battlePassXP: number
    battlePassPremium: boolean
    claimedBattlePassFree: number[]
    claimedBattlePassPremium: number[]
    buyBattlePassPremium: () => void
    buyBattlePassLevels: (amount: number) => void
    claimBattlePassReward: (level: number, type: 'free' | 'premium', reward: any) => void
    username: string
    heroSkin: string
    empireColor: string
    empireEmblem: string
    dailyStats: {
        goldEarned: number
        xpGained: number
        monstersDefeated: number
        gemsSpent: number
        euroSpent: number
    }
    lastDailyReportDay: number
    showDailyReport: boolean
    dailyReportData: any

    // Shop History
    purchasedBundleIds: string[]
    starterPackLevel: number // 0 = not bought yet, 1 = bought starter, 2 = bought advanced, etc.
    megaBundleLevel: number // 0 = initial, 1 = bought once, etc.
    advanceStarterPack: () => void
    advanceMegaBundle: () => void

    // Owned Skins (for shop)
    ownedHeroSkins: string[]
    ownedEmpireColors: string[]
    ownedEmpireEmblems: string[]

    // Bank
    loan: number
    loanPaymentRate: number
    bankTutorialSeen: boolean
    grandmaLastAsk: number | null
    grandmaAngryUntil: number | null

    // Battle
    battleStage: number
    maxStageReached: number
    battleStageWins: Record<number, number>
    autoProgress: boolean

    // Player XP & Level
    playerLevel: number
    playerXP: number
    xpToNextLevel: number
    statPoints: number
    stats: {
        atk: number
        def: number
        spd: number
    }
    distributeStatPoint: (stat: 'atk' | 'def' | 'spd') => void
    addGems: (amount: number) => void
    setEnergy: (amount: number) => void
    gachaPity: number

    // Inventory
    inventory: Item[]
    equipment: Record<string, Item | null>
    equipItem: (item: Item) => void
    unequipItem: (slot: string) => void
    addItem: (item: Item) => void

    // Actions
    setScreen: (screen: GameScreen) => void
    setCustomization: (data: Partial<{ username: string; heroSkin: string; empireColor: string; empireEmblem: string }>) => void
    setPhase: (phase: GamePhase) => void
    startGame: () => void

    // Skin Unlock Actions
    unlockHeroSkin: (skinId: string) => void
    unlockEmpireColor: (colorId: string) => void
    unlockEmpireEmblem: (emblemId: string) => void

    // Resource Actions
    spendGems: (amount: number) => boolean
    addGold: (amount: number) => void
    spendGold: (amount: number) => boolean
    addEuro: (amount: number) => void
    spendEuro: (amount: number) => boolean
    useEnergy: (amount: number) => boolean

    // Building Actions
    startUpgrade: (buildingId: string) => void
    finishUpgrade: (buildingId: string) => void
    passiveTick: () => void

    // Bank Actions
    takeLoan: (amount: number) => void
    askGrandma: () => { success: boolean; amount?: number; angry?: boolean }
    processLoanPayment: () => void
    setBankTutorialSeen: () => void

    // Power & Progress
    addPower: (amount: number) => void
    setRank: (rank: number) => void
    setBattleStage: (stage: number) => void
    incrementStageWins: (stage: number) => void
    addXP: (amount: number) => void
    setAutoProgress: (active: boolean) => void

    // Gacha Actions
    setGachaPity: (pity: number) => void

    // Tracking Actions
    trackMoneySpent: (amount: number) => void
    trackAdWatched: () => void
    trackWaitTime: (ms: number) => void

    // Tutorial
    advanceTutorial: () => void
    completeTutorial: () => void

    // Extended Tutorial Actions
    setTutorialPhase: (phase: TutorialPhase) => void
    setTutorialSubStep: (step: number) => void
    advanceTutorialSubStep: () => void
    setTutorialWonItem: (item: Item | null) => void
    skipTutorial: () => void

    // VIP
    activateVIP: (durationHours: number, level?: number) => void
    upgradeVIP: (level: number) => void

    // Lootbox Actions
    addFreeLootboxSpins: (amount: number) => void
    useFreeLootboxSpin: () => boolean

    // Reset
    resetGame: () => void
}

// ============================================
// INITIAL STATE
// ============================================

const initialResources: ResourceBag = {
    gold: 1000,
    gems: 0,
    euro: 0,
    energy: 50,
    maxEnergy: 50,
}

const generateRandomUsername = () => `User#${Math.floor(10000 + Math.random() * 90000)}`

const initialBuildings: Building[] = [
    {
        id: 'townhall',
        name: 'Rathaus',
        description: 'Verwaltet dein Dorf. Upgrade für neue Gebäude!',
        emoji: '🏰',
        level: 1,
        maxLevel: 20,
        upgradeTimeSeconds: 300,
        upgradeCostGold: 500,
        upgradeCostGems: 50,
        powerBonus: 0,
    },
    {
        id: 'barracks',
        name: 'Kaserne',
        description: 'Erhöht max. Energie und Regeneration.',
        emoji: '⚔️',
        level: 1,
        maxLevel: 20,
        upgradeTimeSeconds: 45,
        upgradeCostGold: 200,
        upgradeCostGems: 20,
        powerBonus: 200,
        energyMax: 10,
    },
    {
        id: 'goldmine',
        name: 'Goldmine',
        description: 'Produziert passiv Gold.',
        emoji: '⛏️',
        level: 1,
        maxLevel: 20,
        upgradeTimeSeconds: 30,
        upgradeCostGold: 100,
        upgradeCostGems: 10,
        powerBonus: 100,
        productionRate: 10,
    },
    {
        id: 'warehouse',
        name: 'Lagerhaus',
        description: 'Lagert Items und Ausrüstung.',
        emoji: '📦',
        level: 1,
        maxLevel: 20,
        upgradeTimeSeconds: 40,
        upgradeCostGold: 150,
        upgradeCostGems: 15,
        powerBonus: 150,
        capacity: 10,
    },
]

const INITIAL_ITEMS: Item[] = [
    { id: 'sword2', name: 'Ebenholz-Stab', emoji: '🪄', slot: 'weapon', power: 45, rarity: 'rare' },
    { id: 'helmet1', name: 'Visier des Ritters', emoji: '🪖', slot: 'head', power: 22, rarity: 'uncommon' },
    { id: 'cape1', name: 'Schattenumhang', emoji: '🧣', slot: 'cape', power: 15, rarity: 'uncommon' },
    { id: 'ring1', name: 'Goldener Siegelring', emoji: '💍', slot: 'ring', power: 8, rarity: 'rare' },
]

const INITIAL_EQUIPMENT: Record<string, Item | null> = {
    head: null,
    cape: null,
    weapon: { id: 'sword1', name: 'Rostiges Schwert', emoji: '🗡️', slot: 'weapon', power: 10, rarity: 'common' },
    armor: { id: 'armor1', name: 'Lederrüstung', emoji: '🥋', slot: 'armor', power: 15, rarity: 'common' },
    shoes: null,
    ring: null,
}

// ============================================
// STORE
// ============================================

export const useGameStore = create<GameState>()(
    devtools(
        persist(
            (set, get) => ({
                // Initial State
                currentScreen: 'landing',
                currentPhase: 'pre-game',
                resources: { ...initialResources },
                power: 100,
                buildings: initialBuildings.map(b => ({ ...b })),
                inventory: INITIAL_ITEMS,
                equipment: INITIAL_EQUIPMENT,
                tutorialStep: 0,
                tutorialComplete: false,

                // Extended Tutorial
                tutorialPhase: 'not-started' as TutorialPhase,
                tutorialSubStep: 0,
                tutorialWonItem: null,

                currentRank: 100,
                targetRank: 1,
                gameStartTime: null,
                realMoneySpent: 0,
                adsWatched: 0,
                totalWaitTimeMs: 0,
                totalGameMinutes: 0,
                isVIP: false,
                vipLevel: 1,
                vipExpiresAtGameMinute: null,
                freeLootboxSpins: 0,
                dailyStreak: 0,
                lastDailyClaimGameDay: null,
                lastDailyClaimType: null,
                battlePassXP: 0,
                battlePassPremium: false,
                claimedBattlePassFree: [],
                claimedBattlePassPremium: [],

                // Shop History
                purchasedBundleIds: [],
                starterPackLevel: 0,
                megaBundleLevel: 0,

                battleStage: 1,
                maxStageReached: 1,
                battleStageWins: {},
                autoProgress: false,
                playerLevel: 1,
                playerXP: 0,
                xpToNextLevel: 250,
                statPoints: 0,
                stats: { atk: 10, def: 10, spd: 10 },
                gachaPity: 0,
                username: generateRandomUsername(),
                heroSkin: 'default',
                empireColor: '#3b82f6',
                empireEmblem: '🏰',
                ownedHeroSkins: ['default'],
                ownedEmpireColors: ['blue'],
                ownedEmpireEmblems: ['castle'],
                loan: 0,
                loanPaymentRate: 0,
                bankTutorialSeen: false,
                grandmaLastAsk: null,
                grandmaAngryUntil: null,
                activeUpgrades: {},

                // Daily Report System
                dailyStats: {
                    goldEarned: 0,
                    xpGained: 0,
                    monstersDefeated: 0,
                    gemsSpent: 0,
                    euroSpent: 0
                },
                lastDailyReportDay: 0,
                showDailyReport: false,
                dailyReportData: null,

                resetGame: () => {
                    set({
                        currentScreen: 'landing',
                        currentPhase: 'pre-game',
                        resources: { ...initialResources },
                        power: 100,
                        buildings: initialBuildings.map(b => ({ ...b })),
                        inventory: [...INITIAL_ITEMS],
                        equipment: { ...INITIAL_EQUIPMENT },
                        tutorialStep: 0,
                        tutorialComplete: false,
                        tutorialPhase: 'not-started',
                        tutorialSubStep: 0,
                        tutorialWonItem: null,
                        currentRank: 100,
                        targetRank: 1,
                        gameStartTime: null,
                        realMoneySpent: 0,
                        adsWatched: 0,
                        totalWaitTimeMs: 0,
                        totalGameMinutes: 0,
                        isVIP: false,
                        vipLevel: 1,
                        vipExpiresAtGameMinute: null,
                        freeLootboxSpins: 0,
                        dailyStreak: 0,
                        lastDailyClaimGameDay: null,
                        lastDailyClaimType: null,
                        battlePassXP: 0,
                        battlePassPremium: false,
                        claimedBattlePassFree: [],
                        claimedBattlePassPremium: [],
                        purchasedBundleIds: [],
                        starterPackLevel: 0,
                        megaBundleLevel: 0,
                        battleStage: 1,
                        maxStageReached: 1,
                        battleStageWins: {},
                        autoProgress: false,
                        playerLevel: 1,
                        playerXP: 0,
                        xpToNextLevel: 250,
                        statPoints: 0,
                        stats: { atk: 10, def: 10, spd: 10 },
                        gachaPity: 0,
                        username: generateRandomUsername(),
                        heroSkin: 'default',
                        empireColor: '#3b82f6',
                        empireEmblem: '🏰',
                        ownedHeroSkins: ['default'],
                        ownedEmpireColors: ['blue'],
                        ownedEmpireEmblems: ['castle'],
                        loan: 0,
                        loanPaymentRate: 0,
                        bankTutorialSeen: false,
                        grandmaLastAsk: null,
                        grandmaAngryUntil: null,
                        activeUpgrades: {},
                        dailyStats: {
                            goldEarned: 0,
                            xpGained: 0,
                            monstersDefeated: 0,
                            gemsSpent: 0,
                            euroSpent: 0,
                        },
                        lastDailyReportDay: 0,
                        showDailyReport: false,
                        dailyReportData: null
                    })
                },

                // Actions
                setScreen: (screen) => set({ currentScreen: screen }),
                activeShopTab: 'bundles' as 'bundles' | 'packs' | 'passes' | 'skins',
                setShopTab: (tab) => set({ activeShopTab: tab }),
                setPhase: (phase) => set({ currentPhase: phase }),
                setCustomization: (data) => set(state => ({ ...state, ...data })),
                startGame: () => set({ currentScreen: 'smsintro', gameStartTime: Date.now() }),

                addGems: (amount) => set(state => ({
                    resources: { ...state.resources, gems: state.resources.gems + amount }
                })),
                spendGems: (amount) => {
                    const state = get()
                    if (state.resources.gems < amount) return false
                    set({
                        resources: { ...state.resources, gems: state.resources.gems - amount },
                        dailyStats: { ...(state as any).dailyStats, gemsSpent: ((state as any).dailyStats?.gemsSpent || 0) + amount }
                    })
                    return true
                },
                addGold: (amount) => set(state => ({
                    resources: { ...state.resources, gold: state.resources.gold + amount }
                })),


                spendGold: (amount) => {
                    const state = get()
                    if (state.resources.gold < amount) return false
                    set({ resources: { ...state.resources, gold: state.resources.gold - amount } })
                    return true
                },
                addEuro: (amount) => set(state => ({
                    resources: { ...state.resources, euro: Math.round((state.resources.euro + amount) * 100) / 100 }
                })),
                spendEuro: (amount) => {
                    const state = get()
                    if (state.resources.euro < amount) return false
                    set({ resources: { ...state.resources, euro: Math.round((state.resources.euro - amount) * 100) / 100 } })
                    return true
                },
                setEnergy: (amount) => set(state => ({
                    resources: { ...state.resources, energy: Math.min(amount, state.resources.maxEnergy) }
                })),
                useEnergy: (amount) => {
                    const state = get()
                    if (state.resources.energy < amount) return false
                    set({ resources: { ...state.resources, energy: state.resources.energy - amount } })
                    return true
                },

                startUpgrade: (buildingId) => set(state => {
                    const building = state.buildings.find(b => b.id === buildingId)
                    if (!building || (state.activeUpgrades && state.activeUpgrades[buildingId])) return {}
                    if (state.resources.gold < building.upgradeCostGold) return {}

                    // Upgrade Slot Limit: 1 for normal players, progressively more for VIP levels
                    const activeUpgradeCount = Object.keys(state.activeUpgrades || {}).length
                    let maxSlots = 1 // Base
                    if (state.isVIP) {
                        if (state.vipLevel === 3) maxSlots = 4
                        else if (state.vipLevel === 2) maxSlots = 3
                        else maxSlots = 2
                    }
                    if (activeUpgradeCount >= maxSlots) return {}

                    // Townhall Requirement: Other buildings cannot exceed Townhall level
                    const townhall = state.buildings.find(b => b.id === 'townhall')
                    if (buildingId !== 'townhall' && townhall && building.level >= townhall.level) {
                        return {}
                    }

                    return {
                        resources: { ...state.resources, gold: state.resources.gold - building.upgradeCostGold },
                        activeUpgrades: { ...(state.activeUpgrades || {}), [buildingId]: Date.now() + (building.upgradeTimeSeconds * 1000) }
                    }
                }),
                finishUpgrade: (buildingId) => set(state => {
                    const { [buildingId]: _, ...remainingUpgrades } = state.activeUpgrades
                    const newBuildings = state.buildings.map(b => {
                        if (b.id === buildingId) {
                            return {
                                ...b,
                                level: b.level + 1,
                                powerBonus: Math.round(b.powerBonus * 1.2),
                                upgradeCostGold: Math.round(b.upgradeCostGold * 1.5),
                                upgradeCostGems: Math.round(b.upgradeCostGems * 1.2),
                                upgradeTimeSeconds: Math.round(b.upgradeTimeSeconds * 1.1),
                                ...(b.productionRate ? { productionRate: Math.round(b.productionRate * 1.3) } : {}),
                                ...(b.capacity ? { capacity: Math.round(b.capacity * 1.5) } : {}),
                                ...(b.energyMax ? { energyMax: Math.round(b.energyMax * 1.2) } : {}),
                            }
                        }
                        return b
                    })
                    const oldBuilding = state.buildings.find(b => b.id === buildingId)
                    const gain = oldBuilding ? Math.round(oldBuilding.powerBonus * 0.2) : 0
                    return {
                        activeUpgrades: remainingUpgrades,
                        buildings: newBuildings,
                        power: state.power + gain
                    }
                }),

                passiveTick: () => {
                    set(state => {
                        const nextMinutes = state.totalGameMinutes + 1
                        let isVIP = state.isVIP
                        let vipLevel = state.vipLevel
                        let vipExpiresAtGameMinute = state.vipExpiresAtGameMinute
                        if (isVIP && vipExpiresAtGameMinute !== null && nextMinutes >= vipExpiresAtGameMinute) {
                            isVIP = false
                            vipLevel = 1 // Reset to level 1 when VIP expires
                            vipExpiresAtGameMinute = null
                        }
                        let newEnergy = state.resources.energy
                        if (nextMinutes % 10 === 0 && newEnergy < state.resources.maxEnergy) {
                            newEnergy = Math.min(newEnergy + 1, state.resources.maxEnergy)
                        }

                        // Day Change Detection (every 24 ingame minutes = 1 day)
                        const currentDay = Math.floor(nextMinutes / (24 * 60))
                        const previousDay = Math.floor(state.totalGameMinutes / (24 * 60))

                        if (currentDay > previousDay && currentDay > 0) {
                            // New day started - trigger daily report
                            return {
                                totalGameMinutes: nextMinutes,
                                isVIP,
                                vipLevel,
                                vipExpiresAtGameMinute,
                                resources: { ...state.resources, energy: newEnergy },
                                showDailyReport: true,
                                dailyReportData: { ...state.dailyStats }, // Snapshot
                                lastDailyReportDay: currentDay,
                                dailyStats: {
                                    goldEarned: 0,
                                    xpGained: 0,
                                    monstersDefeated: 0,
                                    gemsSpent: 0,
                                    euroSpent: 0
                                }
                            }
                        }

                        return { totalGameMinutes: nextMinutes, isVIP, vipLevel, vipExpiresAtGameMinute, resources: { ...state.resources, energy: newEnergy } }
                    })

                    const state = get()
                    const now = Date.now()
                    if (state.activeUpgrades) {
                        Object.entries(state.activeUpgrades).forEach(([id, finishTime]) => {
                            if (finishTime <= now) state.finishUpgrade(id)
                        })
                    }

                    set(state => {
                        const goldMine = state.buildings.find(b => b.id === 'goldmine')
                        const income = goldMine ? (goldMine.productionRate || 0) : 0
                        if (income > 0) {
                            // Track daily gold income
                            return {
                                resources: { ...state.resources, gold: state.resources.gold + income },
                                dailyStats: { ...state.dailyStats, goldEarned: state.dailyStats.goldEarned + income }
                            }
                        }
                        return {}
                    })

                    // Background Farming
                    set(state => {
                        if (!state.autoProgress || !state.isVIP) return {}
                        if (state.totalGameMinutes % 3 === 0) {
                            const currentStage = state.battleStage
                            const energyCost = 5
                            if (state.resources.energy < energyCost) return { autoProgress: false }

                            const enemyIndex = Math.min(Math.floor(currentStage / 5), ENEMIES_FOR_STORE.length - 1)
                            const baseEnemy = ENEMIES_FOR_STORE[enemyIndex]
                            const scaling = 1 + (currentStage * 0.1)
                            const enemyPower = Math.floor(baseEnemy.basePower * scaling)
                            const eqPower = Object.values(state.equipment).reduce((s, i) => s + (i?.power || 0), 0)
                            const totalPower = state.power + eqPower

                            if (totalPower > enemyPower) {
                                const goldReward = Math.floor(baseEnemy.goldReward * scaling)
                                const expReward = Math.floor(baseEnemy.expReward * scaling)
                                const currentWins = state.battleStageWins[currentStage] || 0
                                const nextWins = currentWins + 1
                                let nextMax = state.maxStageReached
                                if (currentStage === state.maxStageReached && nextWins >= 5) nextMax = state.maxStageReached + 1

                                let nextXP = state.playerXP + expReward
                                let nextLevel = state.playerLevel
                                let nextXPToNext = state.xpToNextLevel
                                let nextStatPoints = state.statPoints
                                while (nextXP >= nextXPToNext) {
                                    nextXP -= nextXPToNext
                                    nextLevel += 1
                                    nextStatPoints += 5
                                    nextXPToNext = Math.round(nextXPToNext * 1.5)
                                }
                                return {
                                    resources: { ...state.resources, gold: state.resources.gold + goldReward, energy: state.resources.energy - energyCost },
                                    playerXP: nextXP, playerLevel: nextLevel, xpToNextLevel: nextXPToNext, statPoints: nextStatPoints,
                                    power: state.power + 2, battleStageWins: { ...state.battleStageWins, [currentStage]: nextWins }, maxStageReached: nextMax,
                                    dailyStats: {
                                        ...(state as any).dailyStats,
                                        xpGained: ((state as any).dailyStats?.xpGained || 0) + expReward,
                                        monstersDefeated: ((state as any).dailyStats?.monstersDefeated || 0) + 1,
                                        goldEarned: ((state as any).dailyStats?.goldEarned || 0) + goldReward
                                    }
                                }
                            } else {
                                return { autoProgress: false }
                            }
                        }
                        return {}
                    })
                },

                takeLoan: (amount) => {
                    const state = get()
                    const interest = Math.floor(amount * 0.5)
                    const totalDebt = amount + interest
                    const paymentRate = Math.ceil(totalDebt / 20)
                    set({
                        resources: { ...state.resources, euro: state.resources.euro + amount },
                        loan: state.loan + totalDebt,
                        loanPaymentRate: state.loanPaymentRate + paymentRate
                    })
                },
                askGrandma: () => {
                    const state = get()
                    const now = Date.now()
                    if (state.grandmaAngryUntil && now < state.grandmaAngryUntil) return { success: false, angry: true }
                    const PATIENCE_RESET_MS = 10 * 60 * 1000
                    const ANGRY_DURATION_MS = 20 * 60 * 1000
                    let successChance = 100
                    if (state.grandmaLastAsk) {
                        const timeSinceAsk = now - state.grandmaLastAsk
                        if (timeSinceAsk < PATIENCE_RESET_MS) successChance = Math.floor((timeSinceAsk / PATIENCE_RESET_MS) * 100)
                    }
                    if (Math.random() * 100 < successChance) {
                        const gift = Math.floor(50 + Math.random() * 150)
                        set({ resources: { ...state.resources, euro: state.resources.euro + gift }, grandmaLastAsk: now, grandmaAngryUntil: null })
                        return { success: true, amount: gift }
                    } else {
                        set({ grandmaAngryUntil: now + ANGRY_DURATION_MS, grandmaLastAsk: now })
                        return { success: false, angry: true }
                    }
                },
                processLoanPayment: () => set(state => {
                    if (state.loan <= 0) return {}
                    const payment = Math.min(state.loan, state.loanPaymentRate)
                    const nextLoan = state.loan - payment
                    return {
                        resources: { ...state.resources, euro: state.resources.euro - payment },
                        loan: nextLoan,
                        loanPaymentRate: nextLoan <= 0 ? 0 : state.loanPaymentRate
                    }
                }),
                setBankTutorialSeen: () => set({ bankTutorialSeen: true }),

                addPower: (amount) => set(state => ({ power: state.power + amount })),
                setRank: (rank) => set({ currentRank: rank }),
                setBattleStage: (stage) => set({ battleStage: stage }),
                incrementStageWins: (stage) => set(state => {
                    const currentWins = state.battleStageWins[stage] || 0
                    const nextWins = currentWins + 1
                    let nextMax = state.maxStageReached
                    if (stage === state.maxStageReached && nextWins >= 5) nextMax = state.maxStageReached + 1
                    return {
                        battleStageWins: { ...state.battleStageWins, [stage]: nextWins },
                        maxStageReached: nextMax,
                        dailyStats: { ...(state as any).dailyStats, monstersDefeated: ((state as any).dailyStats?.monstersDefeated || 0) + 1 }
                    }
                }),
                addXP: (amount) => set(state => {
                    let nextXP = state.playerXP + amount
                    let nextLevel = state.playerLevel
                    let nextXPToNext = state.xpToNextLevel
                    let nextStatPoints = state.statPoints
                    while (nextXP >= nextXPToNext) {
                        nextXP -= nextXPToNext
                        nextLevel += 1
                        nextStatPoints += 5
                        nextXPToNext = Math.round(nextXPToNext * 1.5)
                    }
                    return {
                        playerXP: nextXP,
                        playerLevel: nextLevel,
                        xpToNextLevel: nextXPToNext,
                        statPoints: nextStatPoints,
                        dailyStats: { ...(state as any).dailyStats, xpGained: ((state as any).dailyStats?.xpGained || 0) + amount }
                    }
                }),
                setAutoProgress: (active) => set({ autoProgress: active }),
                setGachaPity: (pity) => set({ gachaPity: pity }),

                trackMoneySpent: (amount) => set(state => ({
                    realMoneySpent: state.realMoneySpent + amount,
                    dailyStats: { ...(state as any).dailyStats, euroSpent: ((state as any).dailyStats?.euroSpent || 0) + amount }
                })),
                trackAdWatched: () => set(state => ({ adsWatched: state.adsWatched + 1 })),
                trackWaitTime: (ms) => set(state => ({ totalWaitTimeMs: state.totalWaitTimeMs + ms })),

                advanceTutorial: () => set(state => ({ tutorialStep: state.tutorialStep + 1 })),
                completeTutorial: () => set(state => {
                    const newBuildings = state.buildings.map(b =>
                        b.id === 'townhall' ? { ...b, level: 2 } : b
                    )
                    return {
                        tutorialComplete: true,
                        tutorialPhase: 'complete' as TutorialPhase,
                        currentPhase: 'midgame',
                        currentScreen: 'city',
                        buildings: newBuildings
                    }
                }),

                // Extended Tutorial Actions
                setTutorialPhase: (phase) => set({ tutorialPhase: phase, tutorialSubStep: 0 }),
                setTutorialSubStep: (step) => set({ tutorialSubStep: step }),
                advanceTutorialSubStep: () => set(state => ({ tutorialSubStep: state.tutorialSubStep + 1 })),
                setTutorialWonItem: (item) => set({ tutorialWonItem: item }),
                skipTutorial: () => set({
                    tutorialPhase: 'skipped' as TutorialPhase,
                    tutorialComplete: true,
                    currentPhase: 'midgame',
                    currentScreen: 'city'
                }),
                activateVIP: (durationHours, level = 1) => set(state => {
                    const barracks = state.buildings.find(b => b.id === 'barracks')
                    const baseMax = 50 + (barracks?.energyMax || 0)
                    const currentMinutes = state.totalGameMinutes
                    const baseTime = (state.isVIP && state.vipExpiresAtGameMinute && state.vipExpiresAtGameMinute > currentMinutes) ? state.vipExpiresAtGameMinute : currentMinutes

                    // Calculate energy bonus based on VIP level
                    let energyBonus = 50 // Level 1
                    if (level >= 2) energyBonus += 100 // Level 2 adds +100
                    if (level >= 3) energyBonus += 150 // Level 3 adds +150 more

                    return {
                        isVIP: true,
                        vipLevel: Math.max(state.vipLevel, level), // Upgrade to new level if higher
                        vipExpiresAtGameMinute: baseTime + (durationHours * 60),
                        resources: { ...state.resources, maxEnergy: baseMax + energyBonus }
                    }
                }),

                upgradeVIP: (level) => set(state => {
                    if (!state.isVIP || level <= state.vipLevel) return {}
                    const barracks = state.buildings.find(b => b.id === 'barracks')
                    const baseMax = 50 + (barracks?.energyMax || 0)

                    // Calculate energy bonus based on new VIP level
                    let energyBonus = 50 // Level 1
                    if (level >= 2) energyBonus += 100 // Level 2 adds +100
                    if (level >= 3) energyBonus += 150 // Level 3 adds +150 more

                    return {
                        vipLevel: level,
                        resources: { ...state.resources, maxEnergy: baseMax + energyBonus }
                    }
                }),

                addFreeLootboxSpins: (amount) => set(state => ({ freeLootboxSpins: (state.freeLootboxSpins || 0) + amount })),
                useFreeLootboxSpin: () => {
                    const s = get()
                    if ((s.freeLootboxSpins || 0) > 0) {
                        set({ freeLootboxSpins: s.freeLootboxSpins - 1 })
                        return true
                    }
                    return false
                },

                distributeStatPoint: (stat) => set(state => {
                    if (state.statPoints <= 0) return state
                    return { statPoints: state.statPoints - 1, stats: { ...state.stats, [stat]: state.stats[stat] + 1 } }
                }),

                addItem: (item) => set(state => ({ inventory: [...state.inventory, item] })),
                equipItem: (item) => set(state => {
                    const currentEquipped = state.equipment[item.slot]
                    const newInventory = state.inventory.filter(i => i.id !== item.id)
                    if (currentEquipped) newInventory.push(currentEquipped)
                    return { inventory: newInventory, equipment: { ...state.equipment, [item.slot]: item } }
                }),
                unequipItem: (slot) => set(state => {
                    const item = state.equipment[slot]
                    if (!item) return {}
                    return { equipment: { ...state.equipment, [slot]: null }, inventory: [...state.inventory, item] }
                }),

                unlockHeroSkin: (skinId) => set(state => ({
                    ownedHeroSkins: state.ownedHeroSkins.includes(skinId)
                        ? state.ownedHeroSkins
                        : [...state.ownedHeroSkins, skinId]
                })),
                unlockEmpireColor: (colorId) => set(state => ({
                    ownedEmpireColors: state.ownedEmpireColors.includes(colorId)
                        ? state.ownedEmpireColors
                        : [...state.ownedEmpireColors, colorId]
                })),
                unlockEmpireEmblem: (emblemId) => set(state => ({
                    ownedEmpireEmblems: state.ownedEmpireEmblems.includes(emblemId)
                        ? state.ownedEmpireEmblems
                        : [...state.ownedEmpireEmblems, emblemId]
                })),

                buyBattlePassPremium: () => set({ battlePassPremium: true }),
                buyBattlePassLevels: (amount) => set(state => ({ battlePassXP: state.battlePassXP + (amount * 1000) })),
                claimBattlePassReward: (level, type, reward) => set(state => {
                    const isFree = type === 'free'
                    const claimedList = isFree ? state.claimedBattlePassFree : state.claimedBattlePassPremium
                    if (claimedList.includes(level)) return {}

                    if (reward.type === 'gold') state.addGold(reward.amount)
                    else if (reward.type === 'gems') state.addGems(reward.amount)
                    else if (reward.type === 'energy') state.setEnergy(state.resources.energy + reward.amount)
                    else if (['item', 'legendary', 'epic', 'exclusive', 'rare', 'uncommon'].includes(reward.type)) {
                        const rarityMap: Record<string, ItemRarity> = {
                            'legendary': 'legendary',
                            'exclusive': 'legendary',
                            'epic': 'epic',
                            'rare': 'rare',
                            'uncommon': 'uncommon',
                            'item': 'common'
                        }

                        const slots = ['weapon', 'armor', 'head', 'cape', 'ring', 'shoes']
                        const slot = slots[Math.floor(Math.random() * slots.length)]

                        const newItem: Item = {
                            id: `bp-${type}-${level}-${Date.now()}`,
                            name: reward.label || `${reward.type.charAt(0).toUpperCase() + reward.type.slice(1)} Item`,
                            emoji: reward.emoji,
                            slot: slot,
                            power: level * 12 + (isFree ? 10 : 50),
                            rarity: rarityMap[reward.type] || 'common'
                        }
                        state.addItem(newItem)
                    }
                    return { [isFree ? 'claimedBattlePassFree' : 'claimedBattlePassPremium']: [...claimedList, level] }
                }),
                advanceStarterPack: () => set(state => ({ starterPackLevel: state.starterPackLevel + 1 })),
                advanceMegaBundle: () => set(state => ({ megaBundleLevel: state.megaBundleLevel + 1 })),

                claimDailyReward: () => {
                    const state = get()
                    const currentDay = Math.floor(state.totalGameMinutes / (24 * 60))

                    // If already doubled for today, do nothing
                    if (state.lastDailyClaimGameDay === currentDay && state.lastDailyClaimType === 'doubled') return

                    // Check if streak allows increment (if consecutive day)
                    const newStreak = state.lastDailyClaimGameDay === currentDay - 1 ? state.dailyStreak + 1 : (state.lastDailyClaimGameDay === currentDay ? state.dailyStreak : 1)

                    // Determine claim type based on current VIP status
                    const claimType = state.isVIP ? 'doubled' : 'free'

                    set({
                        dailyStreak: newStreak,
                        lastDailyClaimGameDay: currentDay,
                        lastDailyClaimType: claimType
                    })
                },

                upgradeDailyClaimToDoubled: () => {
                    const state = get()
                    const currentDay = Math.floor(state.totalGameMinutes / (24 * 60))
                    if (state.lastDailyClaimGameDay === currentDay && state.lastDailyClaimType === 'free') {
                        set({ lastDailyClaimType: 'doubled' })
                    }
                },

                // SHOP NAVIGATION
                activeShopTab: 'bundles',
                setShopTab: (tab) => set({ activeShopTab: tab }),
                grandmaLastAsk: null, grandmaAngryUntil: null, activeUpgrades: {},
                dailyStats: { goldEarned: 0, xpGained: 0, monstersDefeated: 0, gemsSpent: 0, euroSpent: 0 },
                lastDailyReportDay: 0, showDailyReport: false, dailyReportData: null,
                purchasedBundleIds: [], starterPackLevel: 0, megaBundleLevel: 0
            }),
            {
                name: 'pay2lose-game-storage-v4',
                partialize: (state) => ({
                    currentScreen: state.currentScreen, currentPhase: state.currentPhase, resources: state.resources,
                    power: state.power, buildings: state.buildings, tutorialStep: state.tutorialStep,
                    tutorialComplete: state.tutorialComplete,
                    tutorialPhase: state.tutorialPhase,
                    tutorialSubStep: state.tutorialSubStep,
                    tutorialWonItem: state.tutorialWonItem,
                    currentRank: state.currentRank, gameStartTime: state.gameStartTime,
                    realMoneySpent: state.realMoneySpent, adsWatched: state.adsWatched, totalWaitTimeMs: state.totalWaitTimeMs,
                    totalGameMinutes: state.totalGameMinutes, isVIP: state.isVIP, vipLevel: state.vipLevel, vipExpiresAtGameMinute: state.vipExpiresAtGameMinute,
                    dailyStreak: state.dailyStreak, lastDailyClaimGameDay: state.lastDailyClaimGameDay, username: state.username,
                    loan: state.loan, loanPaymentRate: state.loanPaymentRate, bankTutorialSeen: state.bankTutorialSeen,
                    battleStage: state.battleStage, autoProgress: state.autoProgress, gachaPity: state.gachaPity,
                    battlePassXP: state.battlePassXP, battlePassPremium: state.battlePassPremium,
                    claimedBattlePassFree: state.claimedBattlePassFree, claimedBattlePassPremium: state.claimedBattlePassPremium,
                    inventory: state.inventory, equipment: state.equipment, playerLevel: state.playerLevel,
                    playerXP: state.playerXP, xpToNextLevel: state.xpToNextLevel, statPoints: state.statPoints,
                    stats: state.stats, grandmaLastAsk: state.grandmaLastAsk, grandmaAngryUntil: state.grandmaAngryUntil,
                    ownedHeroSkins: state.ownedHeroSkins, ownedEmpireColors: state.ownedEmpireColors,
                    ownedEmpireEmblems: state.ownedEmpireEmblems,

                    // Added for persistence fix
                    battleStageWins: state.battleStageWins,
                    maxStageReached: state.maxStageReached,
                    freeLootboxSpins: state.freeLootboxSpins,
                    activeUpgrades: state.activeUpgrades,

                    // Daily Report Persistence
                    dailyStats: state.dailyStats,
                    lastDailyReportDay: state.lastDailyReportDay,
                    showDailyReport: state.showDailyReport,
                    dailyReportData: state.dailyReportData,

                    // Shop History
                    purchasedBundleIds: state.purchasedBundleIds,
                    starterPackLevel: state.starterPackLevel,
                    megaBundleLevel: state.megaBundleLevel
                }),
            }
        ),
        { name: 'Pay2Lose Game Store' }
    )
)
